import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated, selectUser } from '@/store/slices/authSlice';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const location = useLocation();

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Redirect to login with return url
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // User data not yet loaded from localStorage (AuthProvider effect hasn't fired yet)
  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // 🔒 SECURITY: Check if user has admin role (admin, super_admin, manager, staff, support)
  const userRole = user.role?.toLowerCase();
  const allowedRoles = ['admin', 'super_admin', 'manager', 'staff', 'support'];

  if (!allowedRoles.includes(userRole)) {
    // Clear invalid session data and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.setItem('logoutReason', 'Your session has expired or you do not have admin access. Please login again.');
    return <Navigate to="/auth/login" replace />;
  }

  // User is authenticated and has admin role
  return <>{children}</>;
};